import React, { useState, useMemo, useCallback } from "react";
import Papa from "papaparse";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { computeAnalytics, AVG_EPISODE_MIN, AVG_MOVIE_MIN } from "./analytics";
import "./App.css";

const RED = "#e50914";
const RED_SOFT = "rgba(229, 9, 20, 0.55)";
const GRID = "rgba(255, 255, 255, 0.08)";
const TICK = "#b3b3b3";

function baseOptions(title) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: !!title, text: title, color: "#fff", font: { size: 16 } },
      tooltip: { intersect: false, mode: "index" },
    },
    scales: {
      x: { ticks: { color: TICK }, grid: { color: GRID } },
      y: { beginAtZero: true, ticks: { color: TICK }, grid: { color: GRID } },
    },
  };
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`stat-card${highlight ? " stat-card--highlight" : ""}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function StatSection({ title, children }) {
  return (
    <section className="stat-section">
      <h3 className="section-title">{title}</h3>
      <div className="stats-grid">{children}</div>
    </section>
  );
}

function App() {
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [subscriptionPrice, setSubscriptionPrice] = useState(7.99);

  const analytics = useMemo(
    () => (rows ? computeAnalytics(rows, Number(subscriptionPrice) || 0) : null),
    [rows, subscriptionPrice]
  );

  const handleFile = useCallback((file) => {
    if (!file) return;
    setError("");
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data || [];
        const looksValid = data.some((r) => r.Title || r.title);
        if (!looksValid) {
          setError(
            "That file doesn't look like a Netflix viewing history export (no Title column)."
          );
          return;
        }
        setRows(data);
        setFileName(file.name);
      },
      error: () => setError("Could not read that file. Please try again."),
    });
  }, []);

  const onInputChange = (e) => handleFile(e.target.files[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };
  const reset = () => {
    setRows(null);
    setFileName("");
    setError("");
  };

  const s = analytics?.summary;
  const fmt = (n, digits = 0) =>
    Number(n).toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });

  return (
    <div className="App">
      <header className="app-header">
        <h1>
          <span className="brand">NETFLIX</span> Viewing Insights
        </h1>
        <p className="tagline">
          See what your streaming habits really look like.
        </p>
      </header>

      {!analytics && (
        <div
          className="dropzone"
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <h2>Upload your viewing activity</h2>
          <p>
            Download your history from{" "}
            <a
              href="https://www.netflix.com/settings/viewed/"
              target="_blank"
              rel="noreferrer"
              className="red-link"
            >
              netflix.com/settings/viewed
            </a>{" "}
            (the <strong>Download all</strong> button) and drop the CSV here.
          </p>
          <label className="upload-btn">
            Choose CSV file
            <input type="file" accept=".csv" onChange={onInputChange} hidden />
          </label>
          <p className="hint">or drag &amp; drop the file anywhere in this box</p>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      {analytics && (
        <>
          <div className="toolbar">
            <span className="file-chip">📄 {fileName}</span>
            <label className="price-label">
              Monthly price&nbsp;$
              <input
                type="number"
                step="0.01"
                min="0"
                value={subscriptionPrice}
                onChange={(e) => setSubscriptionPrice(e.target.value)}
                className="price-input"
              />
            </label>
            <button className="reset-btn" onClick={reset}>
              Upload another file
            </button>
          </div>

          <div className={`verdict verdict--${analytics.verdict.tone}`}>
            <div className="verdict-headline">
              {analytics.verdict.tone === "great" ||
              analytics.verdict.tone === "good"
                ? "✅ "
                : analytics.verdict.tone === "ok"
                ? "⚠️ "
                : "🛑 "}
              {analytics.verdict.headline}
            </div>
            <ul className="verdict-points">
              {analytics.verdict.points.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>

          <StatSection title="💰 Is it worth it?">
            <StatCard
              highlight
              label="Cost per hour watched"
              value={`$${fmt(s.costPerHour, 2)}`}
              sub="the number that matters"
            />
            <StatCard
              label="Wasted on idle months"
              value={`$${fmt(s.wastedSpend, 2)}`}
              sub={`${fmt(s.idleMonths)} months, 0 views`}
            />
            <StatCard
              label="Active vs idle months"
              value={`${fmt(s.activeMonths)} / ${fmt(s.idleMonths)}`}
              sub={`of ${fmt(s.spanMonths)} paid months`}
            />
            <StatCard
              label="Cost per play"
              value={`$${fmt(s.costPerView, 2)}`}
            />
          </StatSection>

          <StatSection title="📈 Trends & seasons">
            <StatCard
              label="Usage trend"
              value={
                s.trendDir === "up"
                  ? `▲ ${fmt(Math.abs(s.trendPct))}%`
                  : s.trendDir === "down"
                  ? `▼ ${fmt(Math.abs(s.trendPct))}%`
                  : "▬ flat"
              }
              sub="recent vs. earlier"
            />
            <StatCard label="Top season" value={s.topSeason} />
            <StatCard label="Busiest month" value={s.topMonth} />
          </StatSection>

          <StatSection title="📺 Activity">
            <StatCard label="Total plays" value={fmt(s.totalViews)} />
            <StatCard label="TV episodes" value={fmt(s.episodeCount)} />
            <StatCard label="Movies" value={fmt(s.movieCount)} />
            <StatCard label="Unique titles" value={fmt(s.uniqueSeries)} />
          </StatSection>

          <StatSection title="🔥 Habits">
            <StatCard
              label="Days with viewing"
              value={fmt(s.daysWatched)}
              sub={`over ${fmt(s.spanDays)} days`}
            />
            <StatCard
              label="Avg / active day"
              value={fmt(s.avgPerActiveDay, 1)}
              sub={`${fmt(s.avgPerMonth, 1)} per month`}
            />
            <StatCard
              label="Longest streak"
              value={`${fmt(s.longestStreak)} days`}
            />
            <StatCard label="Busiest day" value={s.busiestDay || "—"} />
          </StatSection>

          <StatSection title="⏱️ Time watched (estimated)">
            <StatCard
              label="Est. hours watched"
              value={`${fmt(s.estHours)} h`}
              sub={`≈ ${fmt(s.estFullDays, 1)} full days`}
            />
            <StatCard
              label="Est. days (24h)"
              value={fmt(s.estFullDays, 1)}
              sub={`~${AVG_EPISODE_MIN}m/episode, ~${AVG_MOVIE_MIN}m/movie`}
            />
            <StatCard
              label="Avg / active day"
              value={`${fmt(s.avgMinutesPerActiveDay)} min`}
            />
          </StatSection>

          <StatSection title="💳 Cost">
            <StatCard
              highlight
              label="Est. total spent"
              value={`$${fmt(s.totalSpent, 2)}`}
              sub={`${fmt(s.spanMonths)} months paid`}
            />
            <StatCard
              label="On months you watched"
              value={`$${fmt(s.totalSpent - s.wastedSpend, 2)}`}
              sub={`${fmt(s.activeMonths)} active months`}
            />
            <StatCard
              label="Monthly price"
              value={`$${fmt(Number(subscriptionPrice) || 0, 2)}`}
            />
          </StatSection>

          <p className="date-range">
            Data from <strong>{s.firstDate}</strong> to{" "}
            <strong>{s.lastDate}</strong>
          </p>

          <div className="charts">
            <div className="chart-card wide">
              <div className="chart-canvas">
                <Bar
                  data={{
                    labels: analytics.monthly.labels,
                    datasets: [
                      {
                        data: analytics.monthly.totals,
                        backgroundColor: RED_SOFT,
                        borderColor: RED,
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={baseOptions("Plays per month")}
                />
              </div>
            </div>

            <div className="chart-card wide">
              <div className="chart-canvas">
                <Line
                  data={{
                    labels: analytics.monthly.labels,
                    datasets: [
                      {
                        data: analytics.monthly.cumulative,
                        borderColor: RED,
                        backgroundColor: "rgba(229, 9, 20, 0.15)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                      },
                    ],
                  }}
                  options={baseOptions("Cumulative plays over time")}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-canvas">
                <Bar
                  data={{
                    labels: analytics.topSeries.labels,
                    datasets: [
                      {
                        data: analytics.topSeries.totals,
                        backgroundColor: RED_SOFT,
                        borderColor: RED,
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    ...baseOptions("Top 10 most-watched titles"),
                    indexAxis: "y",
                  }}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-canvas">
                <Bar
                  data={{
                    labels: analytics.weekday.labels,
                    datasets: [
                      {
                        data: analytics.weekday.totals,
                        backgroundColor: RED_SOFT,
                        borderColor: RED,
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={baseOptions("When you watch (day of week)")}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-canvas">
                <Doughnut
                  data={{
                    labels: ["TV episodes", "Movies"],
                    datasets: [
                      {
                        data: [s.episodeCount, s.movieCount],
                        backgroundColor: [RED, "#564d4d"],
                        borderColor: "#181818",
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom", labels: { color: "#fff" } },
                      title: {
                        display: true,
                        text: "TV vs. movies",
                        color: "#fff",
                        font: { size: 16 },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="chart-card wide">
              <div className="chart-canvas">
                <Line
                  data={{
                    labels: analytics.monthly.labels,
                    datasets: [
                      {
                        data: analytics.monthly.costPerHour,
                        borderColor: "#4bc0c0",
                        backgroundColor: "rgba(75, 192, 192, 0.15)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                        spanGaps: false,
                      },
                    ],
                  }}
                  options={baseOptions(
                    "Cost per hour watched, by month ($) — lower is better"
                  )}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-canvas">
                <Bar
                  data={{
                    labels: analytics.season.labels,
                    datasets: [
                      {
                        data: analytics.season.hours,
                        backgroundColor: [
                          "rgba(120, 180, 255, 0.6)",
                          "rgba(120, 220, 150, 0.6)",
                          "rgba(255, 200, 90, 0.6)",
                          "rgba(210, 140, 90, 0.6)",
                        ],
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={baseOptions("Hours watched by season")}
                />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-canvas">
                <Bar
                  data={{
                    labels: analytics.monthOfYear.labels,
                    datasets: [
                      {
                        data: analytics.monthOfYear.totals,
                        backgroundColor: RED_SOFT,
                        borderColor: RED,
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={baseOptions(
                    "Seasonality — plays by calendar month (all years)"
                  )}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <footer className="app-footer">
        Your file is processed entirely in your browser — nothing is uploaded.
      </footer>
    </div>
  );
}

export default App;
